import dayjs from 'dayjs'
import advancedFormat from 'dayjs/plugin/advancedFormat'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'

import permanentDstPlugin from '../../../ts-utils/dayjs-permanent-dst'
import { isTimeToBeDefined } from '../../../ts-utils/date-utils'

dayjs.extend(utc)
dayjs.extend(timezone)
// Keeps zones that abolished their clock change reading correctly, and labelled
// the way people there label them, on a runtime whose timezone database still
// has the old rule — see ts-utils/dayjs-permanent-dst. The correction only
// reaches values that named a zone, which is why `venueZone` is not optional
// sugar here: a bare dayjs(startsAt) renders a BC November game an hour early.
dayjs.extend(permanentDstPlugin)
// `z` (the zone abbreviation, e.g. PDT) only exists with advancedFormat.
dayjs.extend(advancedFormat)

/** e.g. '7:45 PM PDT'. */
export const TIME_WITH_ZONE = 'h:mm A z'
/** e.g. 'Nov 1, 2026' — the header uppercases it. */
export const SHORT_DATE = 'MMM D, YYYY'

/**
 * `.tz()` guarded, because it throws rather than degrading.
 *
 * An empty string is called out separately: `venue?.timezone || ''` is a common
 * shape in this codebase and dayjs raises a RangeError on `.tz('')`, which
 * takes the whole header down instead of showing one time in the wrong zone.
 */
const zonedOrLocal = (instant: dayjs.Dayjs, zone?: string | null) => {
  if (typeof zone !== 'string' || zone.length === 0) {
    return instant
  }

  try {
    return instant.tz(zone)
  } catch {
    // Not a zone this runtime knows. The instant is still real, so show it on
    // the viewer's clock rather than failing the render.
    return instant
  }
}

export interface GameHeaderDateTime {
  /** Venue-zone time with its abbreviation. Empty when the time is TBD. */
  time: string
  /** Venue-zone calendar date, uppercased for the header. */
  date: string
  /** True when `startsAt` carries the "time to be defined" sentinel. */
  isTimeToBeDefined: boolean
}

/**
 * The date and time line the game header shows, in the VENUE's zone.
 *
 * The venue's zone, not the viewer's: a game is anchored to the rink it is
 * played at, so two admins in different provinces must not see it on different
 * calendar days. The zone abbreviation rides along because a bare clock time
 * read from another province is a guess.
 *
 * @param startsAt - ISO 8601 instant, as stored
 * @param venueZone - IANA zone of the venue. Falls back to the viewer's own
 * clock when absent or unknown to this runtime, which is wrong-but-readable
 * rather than a failed render.
 * @returns the display strings, or null when there is no usable start
 */
export const formatGameHeaderDateTime = (
  startsAt?: string | null,
  venueZone?: string | null
): GameHeaderDateTime | null => {
  if (typeof startsAt !== 'string' || startsAt.length === 0) {
    return null
  }

  const instant = dayjs(startsAt)
  if (!instant.isValid()) {
    return null
  }

  const zoned = zonedOrLocal(instant, venueZone)
  const timeToBeDefined = isTimeToBeDefined(startsAt)

  return {
    time: timeToBeDefined ? '' : zoned.format(TIME_WITH_ZONE),
    date: zoned.format(SHORT_DATE).toUpperCase(),
    isTimeToBeDefined: timeToBeDefined,
  }
}
